import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTemplateContextInfo } from '../../contexts/TemplateContext';

const ROUTE_LABELS: Record<string, string> = {
  marketplace: 'Marketplace',
  finops: 'AI Governance',
  catalog: 'Catalog',
  sts: 'STS',
  creative: 'Creative',
  finance: 'Finance',
  media: 'Media',
  dx: 'DX',
  home: 'Home',
  'user-metrics': 'Metrics',
  approvals: 'Approvals',
  'dev-hub': 'Dev Hub',
  'request-access': 'Request Access',
  'fraudulant-email-check': 'Fraudulent Email Check',
  'script-download': 'Script Download',
  'agent-chat': 'Agent Chat',
  docs: 'Docs',
  create: 'Create',
  'catalog-import': 'Register Component',
  'api-docs': 'API Docs',
  'catalog-graph': 'Catalog Graph',
  search: 'Search',
  'infrastructure-stats': 'Insights',
  'task-dashboard': 'Task Dashboard',
  'requirement-agent': 'Requirement Agent',
  reporting: 'Reporting',
  'lifecycle-management': 'Lifecycle Management',
  'infra-provisioning': 'Infra Provisioning',
  operations: 'Operations & Maintenance',
  aws: 'AWS',
  azure: 'Azure',
  gcp: 'GCP',
  'on-premises': 'On-Premises',
  'patch-management': 'Patch Management',
  'change-management': 'Change Management',
  linux: 'Linux',
  windows: 'Windows',
  templates: 'Templates',
  tasks: 'Tasks',
  executions: 'Executions',
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 0',
    marginBottom: '8px',
    borderBottom: '1px solid #e5e7eb',
  },
  rootNoMargin: {
    marginBottom: '0 !important',
  },
  crumb: {
    fontSize: '13px',
    fontWeight: 400,
    color: '#9ca3af',
    cursor: 'pointer',
    '&:hover': { color: '#111827' },
  },
  current: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#111827',
  },
  separator: {
    fontSize: '13px',
    color: '#d1d5db',
    userSelect: 'none',
  },
});

function buildCrumbs(pathname: string) {
  const segments = pathname.replace(/\/$/, '').split('/').filter(Boolean);
  const crumbs: { label: string; path: string }[] = [];
  let accumulated = '';

  for (const segment of segments) {
    accumulated += `/${segment}`;
    const label =
      ROUTE_LABELS[segment] ??
      segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    crumbs.push({ label, path: accumulated });
  }

  return crumbs;
}

const TAG_TO_PATH: Record<string, string> = {
  aws:               '/sts/infra-provisioning/aws',
  azure:             '/sts/infra-provisioning/azure',
  gcp:               '/sts/infra-provisioning/gcp',
  'on-premises':     '/sts/infra-provisioning/on-premises',
  'patch-management':  '/sts/operations/patch-management',
  'change-management': '/sts/operations/change-management',
  linux:             '/sts/operations/linux',
  windows:           '/sts/operations/windows',
};

function formatName(name: string) {
  return name.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export const Breadcrumbs = ({ templateTags, disableMargin }: { templateTags?: string[]; disableMargin?: boolean } = {}) => {
  const classes = useStyles();
  const { pathname, state } = useLocation() as { pathname: string; state: any };
  const navigate = useNavigate();
  const { derivedPath, loading } = useTemplateContextInfo();

  const templateMatch = pathname.match(/^\/sts\/create\/templates\/([^/]+)\/([^/]+)/);
  const taskMatch = pathname.match(/^\/sts\/create\/tasks\/([^/]+)/);
  const catalogEntityMatch = pathname.match(/^\/marketplace\/docs\/catalog\/[^/]+\/[^/]+\/([^/]+)/);
  const techDocsMatch = !catalogEntityMatch && pathname.match(/^\/marketplace\/docs\/([^/]+)\/([^/]+)\/([^/]+)/);
  const agentChatMatch = pathname.match(/^\/marketplace\/home\/agent-chat\/([^/]+)/);
  const stsAgentChatTemplateMatch = pathname.match(/^\/sts\/agent-chat\/[^/]+\/([^/]+)/);
  const stsAgentChatAdminMatch = pathname === '/sts/agent-chat/admin';
  const marketplaceAgentChatAdminMatch = pathname === '/marketplace/home/agent-chat/admin';
  const requirementAgentMatch = pathname.match(/^\/sts\/requirement-agent(\/([^/]+))?/);

  const resolvedFrom: string | undefined =
    state?.from ??
    derivedPath ??
    (templateTags?.reduce<string | undefined>(
      (found, tag) => found ?? TAG_TO_PATH[tag],
      undefined,
    ));

  // wait for async catalog lookup before rendering agent-chat/template crumbs
  if (loading && (stsAgentChatTemplateMatch || templateMatch)) return null;

  const crumbs = stsAgentChatAdminMatch
    ? [
        { label: 'STS', path: '/sts' },
        { label: 'Admin', path: pathname },
      ]
    : marketplaceAgentChatAdminMatch
    ? [
        { label: 'Marketplace', path: '/marketplace' },
        { label: 'Admin', path: pathname },
      ]
    : agentChatMatch
    ? [
        { label: 'Marketplace', path: '/marketplace' },
        { label: 'Home', path: '/marketplace/home' },
        { label: ROUTE_LABELS[agentChatMatch[1]] ?? formatName(agentChatMatch[1].replace(/^[^_]+_[^_]+_/, '').replace(/_/g, '-')), path: pathname },
      ]
    : stsAgentChatTemplateMatch
    ? [
        ...buildCrumbs(resolvedFrom ?? '/sts'),
        { label: formatName(stsAgentChatTemplateMatch[1]), path: pathname },
      ]
    : taskMatch
    ? [
        ...buildCrumbs(state?.from ?? '/sts/task-dashboard'),
        { label: taskMatch[1], path: pathname },
      ]
    : templateMatch
    ? [
        ...buildCrumbs(resolvedFrom ?? '/sts'),
        {
          label: ROUTE_LABELS[templateMatch[2]] ?? formatName(templateMatch[2]),
          path: pathname,
        },
      ]
    : catalogEntityMatch
    ? [
        { label: 'Marketplace', path: '/marketplace' },
        { label: 'Docs', path: '/marketplace/docs' },
        { label: formatName(catalogEntityMatch[1]), path: pathname },
      ]
    : techDocsMatch
    ? [
        { label: 'Marketplace', path: '/marketplace' },
        { label: 'Docs', path: '/marketplace/docs' },
        { label: formatName(techDocsMatch[3]), path: pathname },
      ]
    : requirementAgentMatch
    ? [
        { label: 'STS', path: '/sts' },
        { label: 'Infra Provisioning', path: '/sts/infra-provisioning' },
        { label: 'Requirement Agent', path: '/sts/requirement-agent' },
        ...(requirementAgentMatch[2]
          ? [{ label: requirementAgentMatch[2], path: pathname }]
          : []),
      ]
    : buildCrumbs(pathname);

  if (crumbs.length === 0) return null;

  return (
    <Box className={`${classes.root} ${disableMargin ? classes.rootNoMargin : ''}`}>
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <React.Fragment key={crumb.path}>
            {i > 0 && (
              <span className={classes.separator}>/</span>
            )}
            {isLast ? (
              <Typography component="span" className={classes.current}>
                {crumb.label}
              </Typography>
            ) : (
              <Typography
                component="span"
                className={classes.crumb}
                onClick={() => navigate(crumb.path)}
              >
                {crumb.label}
              </Typography>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};
